import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState, type FormEvent, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  customerServicesShellQuery,
  deviceSubtypesQuery,
  isServicesTab,
  newServiceQuery,
  propertiesQuery,
  propertyOptionsQuery,
  removeProperty,
  saveProperty,
  saveService,
  serviceQuery,
  servicesQuery,
  setServiceStatus,
  templatesQuery,
  type PropertyRow,
  type ServicesTab,
  type ServiceTemplate,
} from '../../api/settingsCustomerServices'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { esc } from '../customers/CustomersSettingsPage'
import './CustomerServicesPage.css'

/**
 * Settings -> Customer Services: serviciile pe care furnizorul le vinde, si
 * proprietatile din care se compun.
 *
 * Perechile vechi: `views/version-3/settings/customersV2/services.php`,
 * `addServiceModal.php`, `editServiceModal.php`,
 * `serviceList/service_properties.php`, si `public/js/settings/customers/services.js`.
 *
 * CE POATE ECRANUL
 * ----------------
 *   Services            lista, serviciu nou, editare, Disable / Enable
 *   Service Properties  lista, proprietate noua, editare, stergere
 *
 * CE E ALTFEL, SI DE CE
 * ---------------------
 *  - UN SINGUR FORMULAR pentru adaugare si editare. In portal erau doua
 *    machete cu aceleasi campuri, si se despartisera deja: cea de adaugare avea
 *    Description, cea de editare nu - deci orice editare pierdea descrierea din
 *    ochii omului (in baza ramanea, dar nu se mai putea schimba).
 *  - SUB-CATEGORIA se vede si se salveaza. Fereastra veche desena optiunile
 *    GOALE si `update()` nici nu scria coloana.
 *  - "Disable" / "Enable" chiar functioneaza: `changeStatus()` intreba
 *    `is_array()` pe un obiect, deci raspundea mereu "Service not found!".
 *  - Tabelul cu detaliile sablonului (Category / Priority / Coverage / UM /
 *    SLA) se deseneaza AICI, din campuri. Portalul il lipea ca HTML in
 *    raspunsul de autocomplete, cu numele nescapate, si scria `-` la doua
 *    dintre SLA-uri desi le avea in mana.
 */

const SERVICES_TABLE = 'settings-customer-services'

const PROPERTIES_TABLE = 'settings-customer-service-properties'

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/* -------------------------------------------------------------------------
 * Coloane
 * ---------------------------------------------------------------------- */

/** `ServicesModel` din services.js, coloana cu coloana. */
export function serviceColumns(canEdit: boolean): TableColumn[] {
  const plain = (index: number, title: string, key: string): TableColumn => ({
    index,
    title,
    orderable: true,
    visible: true,
    html: (row) => esc(String(row[key] ?? '')),
  })

  return [
    {
      index: 0,
      title: '',
      orderable: false,
      visible: true,
      width: 30,
      html: (row) => String(row.service_id ?? ''),
    },
    plain(1, 'Name', 'service_name'),
    plain(2, 'Service Category', 'service_category'),
    plain(3, 'Priority', 'priority_name'),
    plain(4, 'Coverage', 'coverage_name'),
    plain(5, 'Response SLA', 'sla_name'),
    plain(6, 'Intervention SLA', 'intervention_sla_name'),
    plain(7, 'Resolve SLA', 'resolve_sla_name'),
    plain(8, 'MeasurementUnit', 'UM_name'),
    plain(9, 'Quantity', 'quantity'),
    {
      index: 10,
      title: 'Cost',
      orderable: true,
      visible: true,
      html: (row) => esc(`${String(row.cost ?? '')} ${String(row.currency ?? '')}`.trim()),
    },
    plain(11, 'Description', 'description'),
    {
      index: 12,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) => {
        if (!canEdit) return ''
        const id = Number(row.service_id)
        const edit = `<button type="button" class="btn btn-xs btn-primary od-edit-service" data-service="${id}"> Edit</button>&nbsp;&nbsp;`
        return (
          edit +
          (Number(row.service_status) === 1
            ? `<button type="button" class="btn btn-xs btn-danger od-service-status" data-service="${id}" data-status="0"><i class="fas fa-times-circle" aria-hidden="true"></i> Disable</button>`
            : `<button type="button" class="btn btn-xs btn-success od-service-status" data-service="${id}" data-status="1"><i class="fas fa-undo" aria-hidden="true"></i> Enable</button>`)
        )
      },
    },
  ]
}

export const SERVICE_ORDER_NAMES = [
  '',
  'service_name',
  'service_category',
  'priority_name',
  'coverage_name',
  'sla_name',
  'intervention_sla_name',
  'resolve_sla_name',
  'UM_name',
  'quantity',
  'cost',
  'description',
  '',
]

/** `service_properties-1.js`, coloana cu coloana. */
export function propertyColumns(canEdit: boolean): TableColumn[] {
  const plain = (index: number, title: string, key: string): TableColumn => ({
    index,
    title,
    orderable: true,
    visible: true,
    html: (row) => esc(String(row[key] ?? '')),
  })

  return [
    { index: 0, title: '', orderable: false, visible: true, width: 30, html: (row) => String(row.id ?? '') },
    plain(1, 'Name', 'name'),
    plain(2, 'Coverage', 'coverage'),
    plain(3, 'Response SLA', 'response_sla'),
    plain(4, 'Intervention SLA', 'intervention_sla'),
    plain(5, 'Resolve SLA', 'resolve_sla'),
    plain(6, 'Created by', 'user_fullname'),
    plain(7, 'Created', 'created_at'),
    {
      index: 8,
      title: 'Actions',
      orderable: false,
      visible: true,
      html: (row) => {
        if (!canEdit) return ''
        const id = Number(row.id)
        return (
          `<button type="button" class="btn btn-xs btn-primary od-edit-property" data-property="${id}"> Edit</button>&nbsp;&nbsp;` +
          `<button type="button" class="btn btn-xs btn-danger od-remove-property" data-property="${id}"><i class="fas fa-times-circle" aria-hidden="true"></i> Delete</button>`
        )
      },
    },
  ]
}

export const PROPERTY_ORDER_NAMES = [
  '',
  'name',
  'coverage',
  'response_sla',
  'intervention_sla',
  'resolve_sla',
  'user_fullname',
  'created_at',
  '',
]

/* -------------------------------------------------------------------------
 * Ecranul
 * ---------------------------------------------------------------------- */

export function CustomerServicesPage({ tab }: { tab: string }) {
  const navigate = useNavigate()
  const shell = useQuery(customerServicesShellQuery)

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!shell.data) return <Spinner />

  const data = shell.data
  const current = isServicesTab(tab) ? tab : 'services'

  return (
    <>
      <nav className="od-tabbar">
        <ul className="od-tabbar-nav">
          <li className="menu-header">Customer services</li>
          {data.tabs.map((one) => (
            <TabLink key={one.id} tab={one} current={current} navigate={navigate} />
          ))}
          <li className="menu-block-divider"></li>
        </ul>
      </nav>
      <div className="page-content-wrapper">
        {!data.ready ? (
          <div className="alert alert-info">
            <i className="fas fa-info-circle" aria-hidden="true"></i> Customer services are not set up on this instance.
          </div>
        ) : null}
        {current === 'services' ? (
          <ServicesPanel canCreate={data.canCreate} canEdit={data.canEdit} currencies={data.currencies} />
        ) : (
          <PropertiesPanel canCreate={data.canCreateProperty} canEdit={data.canEditProperty} />
        )}
      </div>
    </>
  )
}

function TabLink({
  tab,
  current,
  navigate,
}: {
  tab: ServicesTab
  current: string
  navigate: ReturnType<typeof useNavigate>
}) {
  const active = tab.id === current
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    if (tab.id === 'services') {
      void navigate({ to: '/settings/customer_services' })
      return
    }
    void navigate({ to: '/settings/customer_services/$tab', params: { tab: tab.id } })
  }

  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.id} href={'/app/' + tab.href} onClick={onClick}>
        <i className={tab.icon} aria-hidden="true"></i> {tab.label}
      </a>
    </li>
  )
}

/* -------------------------------------------------------------------------
 * Services
 * ---------------------------------------------------------------------- */

function ServicesPanel({
  canCreate,
  canEdit,
  currencies,
}: {
  canCreate: boolean
  canEdit: boolean
  currencies: string[]
}) {
  const query = useQueryClient()
  const [state, setState] = useTableState(SERVICES_TABLE, {
    start: 0,
    length: 25,
    /* `order: [[1, 'desc']]` din services.js. */
    order: { column: 1, dir: 'desc' },
    search: '',
  })
  const columns = useMemo(() => serviceColumns(canEdit), [canEdit])
  const [editing, setEditing] = useState<number | null>(null)

  const table = useQuery({
    ...servicesQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: SERVICE_ORDER_NAMES[state.order.column] ?? 'service_name',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customer-services'] })

  return (
    <div
      onClick={(event) => {
        /* Butoanele coloanei Actions sunt HTML scris de coloana, ca in
           DataTables - un singur ascultator delegat, ca in portal. */
        const target = event.target as HTMLElement
        const edit = target.closest('.od-edit-service')
        if (edit) {
          const id = Number(edit.getAttribute('data-service') ?? 0)
          if (id > 0) setEditing(id)
          return
        }
        const flip = target.closest('.od-service-status')
        if (!flip) return
        const id = Number(flip.getAttribute('data-service') ?? 0)
        const wanted = Number(flip.getAttribute('data-status') ?? 0)
        if (id < 1) return
        void setServiceStatus(id, wanted)
          .then(() => {
            toast('Status changed!', 'Success')
            reload()
          })
          .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      }}
    >
      {canCreate && (
        <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setEditing(0)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New service
        </button>
      )}
      <h4>
        <strong>Services</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage <strong>services</strong>
      </label>
      <DataTable
        id={SERVICES_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.service_id)}`)}
        emptyHint="A service is one line of what you sell a customer."
      />
      <ServiceModal
        serviceId={editing}
        currencies={currencies}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          reload()
        }}
      />
    </div>
  )
}

/** Tabelul mic de sub sablonul ales - ce lipea portalul ca `subtext`. */
function TemplateDetails({ template }: { template: ServiceTemplate }) {
  return (
    <table className="service_template_details">
      <thead>
        <tr>
          <th>Category</th>
          <th>Priority</th>
          <th>Coverage</th>
          <th>UM</th>
          <th>Has quantity</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{template.category}</td>
          <td>{template.priority}</td>
          <td>{template.coverage}</td>
          <td>{template.measure}</td>
          <td>
            {template.hasQuantity ? (
              <i className="fa fa-check fa-2x general_green" aria-hidden="true"></i>
            ) : (
              <i className="fas fa-times fa-2x general_red" aria-hidden="true"></i>
            )}
          </td>
        </tr>
      </tbody>
      <thead>
        <tr>
          <th>Response SLA</th>
          <th>Intervention SLA</th>
          <th>Resolve SLA</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{template.responseSla}</td>
          <td>{template.interventionSla !== '' ? template.interventionSla : '-'}</td>
          <td>{template.resolveSla !== '' ? template.resolveSla : '-'}</td>
        </tr>
      </tbody>
    </table>
  )
}

/**
 * `addServiceModal.php` si `editServiceModal.php`, scrise o singura data.
 * `serviceId === 0` inseamna "serviciu nou", un numar inseamna editare.
 */
function ServiceModal({
  serviceId,
  currencies,
  onClose,
  onSaved,
}: {
  serviceId: number | null
  currencies: string[]
  onClose(): void
  onSaved(): void
}) {
  const open = serviceId !== null
  const editing = (serviceId ?? 0) > 0
  const existing = useQuery(serviceQuery(editing ? (serviceId as number) : 0))
  const blank = useQuery({ ...newServiceQuery, enabled: open && !editing })

  const [search, setSearch] = useState('')
  const found = useQuery({ ...templatesQuery(search), enabled: open })

  const [template, setTemplate] = useState<ServiceTemplate | null>(null)
  const [category, setCategory] = useState('')
  const [subcategory, setSubcategory] = useState('')
  const [quantity, setQuantity] = useState('')
  const [cost, setCost] = useState('')
  const [currency, setCurrency] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const subtypes = useQuery(deviceSubtypesQuery(Number(category) > 0 ? Number(category) : 0))

  useEffect(() => {
    if (!open) return
    setError('')
    setBusy(false)
    setSearch('')
    if (!editing) {
      setTemplate(null)
      setCategory('')
      setSubcategory('')
      setQuantity('')
      setCost('')
      setCurrency('')
      setDescription('')
    }
  }, [open, editing])

  useEffect(() => {
    if (!editing || !existing.data) return
    const one = existing.data.service
    setTemplate(existing.data.template)
    setCategory(one.category > 0 ? String(one.category) : '')
    setSubcategory(one.subcategory > 0 ? String(one.subcategory) : '')
    setQuantity(one.quantity)
    setCost(one.cost)
    setCurrency(one.currency)
    setDescription(one.description)
  }, [editing, existing.data])

  const deviceTypes = editing ? (existing.data?.deviceTypes ?? []) : (blank.data?.deviceTypes ?? [])
  const templateOptions = useMemo(() => {
    const list = (found.data?.templates ?? []).map((one) => ({ value: String(one.id), label: one.name, subtext: one.category }))
    if (template !== null && !list.some((one) => one.value === String(template.id))) {
      list.unshift({ value: String(template.id), label: template.name, subtext: template.category })
    }
    return list
  }, [found.data, template])
  const hasCategory = template === null ? false : template.hasCategory === 1
  const hasQuantity = template === null ? true : template.hasQuantity === 1

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setBusy(true)
    const fields: Record<string, string | number> = {
      template_id: template?.id ?? 0,
      quantity,
      cost,
      currency,
      category: hasCategory ? category : '',
      subcategory: hasCategory ? subcategory : '',
    }
    if (editing) fields.id = serviceId as number
    else fields.description = description

    void saveService(fields)
      .then(() => {
        setBusy(false)
        toast(editing ? 'Service saved.' : 'Service added.', 'Success')
        onSaved()
      })
      .catch((err: unknown) => {
        setBusy(false)
        setError(failed(err))
      })
  }

  return (
    <Modal
      id={editing ? 'modalEditService' : 'modalAddService'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit service' : 'Add new service'}
    >
      <form className="form-horizontal" id="saveServiceForm" onSubmit={submit}>
        {error !== '' ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
          </div>
        ) : null}

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
            <span className="color">Service template</span>
          </label>
          <div className="col-sm-9 service_template_selector">
            {/*
             * `addServiceModal.php` scrie `<select id="service_template_id"
             * class="form-control">`, iar `services.js` il porneste cu
             * `selectpicker({ liveSearch: true, size: 10 })` si
             * `ajaxSelectPicker(...serviceTemplateAutocomplete)`: un buton cu
             * numele ales si, la apasare, o lista cu cautare pe server.
             *
             * Aici statea o casuta de text urmata de un <select size=5> - o lista
             * deschisa de 116px, mereu pe ecran, sub o casuta care nu parea legata
             * de ea. `SelectPicker` e bootstrap-select-ul portalului, cu `onSearch`
             * pe post de ajaxSelectPicker: ce scrie omul pleaca la `templatesQuery`.
             *
             * Sablonul deja ales ramane in optiuni si cand cautarea nu-l mai
             * aduce: altfel butonul unui serviciu deschis la editare ar fi scris
             * titlul, ca si cum n-ar avea niciun sablon.
             */}
            <SelectPicker
              id="service_template_id"
              className="form-control"
              title="Select and Begin Typing"
              size={10}
              options={templateOptions}
              value={template === null ? '' : String(template.id)}
              onSearch={setSearch}
              searching={found.isFetching}
              onChange={(next) => {
                const picked =
                  (found.data?.templates ?? []).find((one) => String(one.id) === next) ??
                  (template !== null && String(template.id) === next ? template : null)
                setTemplate(picked)
                if (picked && picked.hasCategory !== 1) {
                  setCategory('')
                  setSubcategory('')
                }
              }}
            />
            {template === null ? null : <TemplateDetails template={template} />}
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="service_device_category">
            <span className="color">Device Category</span>
          </label>
          <div className="col-sm-9">
            <select
              id="service_device_category"
              className="form-control"
              disabled={!hasCategory}
              value={category}
              onChange={(e) => {
                setCategory(e.target.value)
                setSubcategory('')
                /* Costul implicit al tipului, ca in services.js. */
                const picked = deviceTypes.find((one) => String(one.id) === e.target.value)
                if (picked) {
                  if (cost === '') setCost(picked.cost)
                  if (currency === '') setCurrency(picked.currency)
                }
              }}
            >
              <option value="">Please select</option>
              {deviceTypes.map((one) => (
                <option key={one.id} value={one.id} data-cost={one.cost} data-currency={one.currency}>
                  {one.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="service_device_sub_category">
            <span className="color">Device Sub-Category</span>
          </label>
          <div className="col-sm-9">
            <select
              id="service_device_sub_category"
              className="form-control"
              disabled={!hasCategory || Number(category) < 1}
              value={subcategory}
              onChange={(e) => setSubcategory(e.target.value)}
            >
              <option value="">Please select</option>
              {(subtypes.data?.deviceSubtypes ?? []).map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="service_quantity">
            <span className="color">Quantity</span>
          </label>
          <div className="col-sm-9">
            <input
              id="service_quantity"
              className="form-control"
              disabled={!hasQuantity}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="service_cost">
            <span className="color">Service Cost per unit</span>
          </label>
          <div className="col-sm-9">
            <input id="service_cost" className="form-control" value={cost} onChange={(e) => setCost(e.target.value)} />
          </div>
        </div>

        {editing ? null : (
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="service_desc">
              <span className="color">Description</span>
            </label>
            <div className="col-sm-9">
              <textarea
                id="service_desc"
                className="form-control"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="form-group">
          <label className="col-sm-3 control-label" style={{ textAlign: 'left' }} htmlFor="service_currency">
            <span className="color">Service Currency</span>
          </label>
          <div className="col-sm-9">
            <select
              id="service_currency"
              className="form-control"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="">Select</option>
              {currencies.map((one) => (
                <option key={one} value={one}>
                  {one}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary SaveService" disabled={busy}>
            Save
          </button>
        </div>
      </form>
    </Modal>
  )
}

/* -------------------------------------------------------------------------
 * Service Properties
 * ---------------------------------------------------------------------- */

function PropertiesPanel({ canCreate, canEdit }: { canCreate: boolean; canEdit: boolean }) {
  const query = useQueryClient()
  const [state, setState] = useTableState(PROPERTIES_TABLE, {
    start: 0,
    length: 25,
    order: { column: 1, dir: 'asc' },
    search: '',
  })
  const columns = useMemo(() => propertyColumns(canEdit), [canEdit])
  const [editing, setEditing] = useState<PropertyRow | null>(null)
  const [creating, setCreating] = useState(false)

  const table = useQuery({
    ...propertiesQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: PROPERTY_ORDER_NAMES[state.order.column] ?? 'name',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  if (table.error instanceof ApiError && table.error.status === 403) return <Forbidden />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]
  const reload = () => void query.invalidateQueries({ queryKey: ['settings', 'customer-services'] })

  return (
    <div
      onClick={(event) => {
        const target = event.target as HTMLElement
        const edit = target.closest('.od-edit-property')
        if (edit) {
          const id = Number(edit.getAttribute('data-property') ?? 0)
          const row = (table.data?.data ?? []).find((one) => one.id === id)
          if (row) setEditing(row)
          return
        }
        const remove = target.closest('.od-remove-property')
        if (!remove) return
        const id = Number(remove.getAttribute('data-property') ?? 0)
        if (id < 1 || !confirm('Delete this property?')) return
        void removeProperty(id)
          .then(() => {
            toast('Property deleted.', 'Success')
            reload()
          })
          .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      }}
    >
      {canCreate && (
        <button type="button" id="buttonAddProperty" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> New Service Property
        </button>
      )}
      <h4>
        <strong>Services Properties</strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>properties</strong>
      </label>
      <hr />
      <DataTable
        id={PROPERTIES_TABLE}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
        emptyHint="A property is a coverage plus the three SLAs, kept together under a name."
      />
      <PropertyModal
        property={editing}
        open={creating || editing !== null}
        onClose={() => {
          setCreating(false)
          setEditing(null)
        }}
        onSaved={() => {
          setCreating(false)
          setEditing(null)
          reload()
        }}
      />
    </div>
  )
}

/** `serviceList/service_properties.php`, fereastra - una singura, si pentru editare. */
function PropertyModal({
  property,
  open,
  onClose,
  onSaved,
}: {
  property: PropertyRow | null
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const options = useQuery({ ...propertyOptionsQuery, enabled: open })
  const [name, setName] = useState('')
  const [coverage, setCoverage] = useState('')
  const [response, setResponse] = useState('')
  const [intervention, setIntervention] = useState('')
  const [resolve, setResolve] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setError('')
    setBusy(false)
    setName(property?.name ?? '')
    setCoverage(property ? String(property.coverage_id) : '')
    setResponse(property ? String(property.response_sla_id) : '')
    setIntervention(property ? String(property.intervention_sla_id) : '')
    setResolve(property ? String(property.resolve_sla_id) : '')
  }, [open, property])

  const pick = (id: string, label: string, value: string, set: (next: string) => void, list: { id: number; name: string }[]) => (
    <div className="col-md-9">
      <div className="form-group">
        <label className="control-label" htmlFor={id}>
          {label}
          <span className="required">*</span>
        </label>
        <select id={id} className="form-control input-sm" value={value} onChange={(e) => set(e.target.value)}>
          <option value=""> - select - </option>
          {list.map((one) => (
            <option key={one.id} value={one.id}>
              {one.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  )

  return (
    <Modal
      id="propertyModal"
      open={open}
      onClose={onClose}
      title={property === null ? 'Add New Properties' : 'Edit property'}
      large
    >
      <form
        id="property-form"
        onSubmit={(event) => {
          event.preventDefault()
          setError('')
          setBusy(true)
          const fields: Record<string, string | number> = {
            property_name: name,
            property_coverage: coverage,
            response_sla: response,
            intervention_sla: intervention,
            resolve_sla: resolve,
          }
          if (property !== null) fields.id = property.id
          void saveProperty(fields)
            .then(() => {
              setBusy(false)
              toast(property === null ? 'Property created.' : 'Property updated.', 'Success')
              onSaved()
            })
            .catch((err: unknown) => {
              setBusy(false)
              setError(failed(err))
            })
        }}
      >
        {error !== '' ? (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
          </div>
        ) : null}
        <div className="row">
          <div className="col-md-9">
            <div className="form-group">
              <label className="control-label" htmlFor="property_name">
                Name <span className="required">*</span>
              </label>
              <input
                id="property_name"
                type="text"
                className="form-control input-sm"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
          {pick('property_coverage', 'Coverage', coverage, setCoverage, options.data?.coverages ?? [])}
          {pick('response_sla', 'Response SLA', response, setResponse, options.data?.responseSla ?? [])}
          {pick('intervention_sla', 'Intervention SLA', intervention, setIntervention, options.data?.interventionSla ?? [])}
          {pick('resolve_sla', 'Resolve SLA', resolve, setResolve, options.data?.resolveSla ?? [])}
          <div className="clearfix"></div>
          <div className="col-md-12">
            <div className="form-group aright">
              <button type="button" className="btn btn-default" onClick={onClose}>
                Close
              </button>{' '}
              <button type="submit" className="btn btn-primary" id="new-property-btn" disabled={busy}>
                Save
              </button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  )
}
